import { useCallback, useMemo, useState } from 'react'
import { Platform, Pressable, StyleSheet, Text } from 'react-native'
import { ChevronsUpDown } from 'lucide-react-native'
import { useTheme } from '../../../theme/useTheme'
import { useAccountSwitcher } from '../../contexts/AccountSwitcherContext'
import { useAvatarContext } from '../../../avatars/contexts/AvatarContext'
import { OrgSwitcherSheet } from './OrgSwitcherSheet'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { FriendAvatar } from '../../../../components/friends/shared/FriendAvatar'
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types'

/**
 * Compact account-switcher control for the global menu footer (specs/profile-page AC-20..22).
 * Sits to the LEFT of Sign Out. Renders nothing when there is only one account — a
 * single-account user has nothing to switch between, so the footer shows Sign Out alone (AC-21).
 *
 * Unlike {@link OrgSwitcherMenuRow} (which this replaces in the menu), it works on every screen
 * width — it is a deliberate footer affordance, not a wide-web-only header chip.
 */
export function OrgSwitcherFooterButton({
  testID,
  closeHostThen,
}: {
  testID?: string
  /** Closes the menu this button lives in before a switch runs (see OrgSwitcherSheet). */
  closeHostThen?: (then: () => void) => void
}) {
  const theme = useTheme()
  const { accounts, activeUserId } = useAccountSwitcher()
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext()
  const [open, setOpen] = useState(false)

  const activeAccount = useMemo(
    () => accounts.find((a) => a.userId === activeUserId) ?? null,
    [accounts, activeUserId],
  )

  const handlePress = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  // Nothing to switch between → no switcher chrome.
  if (accounts.length < 2) return null

  const label = activeAccount?.displayName || activeAccount?.email || 'Account'
  const avatarId = (currentAvatarId ?? activeAccount?.zportiAvatarId ?? null) as DisplayAvatarId | null

  return (
    <>
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={`Switch account. Signed in as ${label}.`}
        testID={testID ?? 'menu-footer-switcher'}
        style={({ pressed, hovered }) => [
          styles.button,
          { backgroundColor: theme.background.card, borderColor: theme.gray[200] },
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.buttonPressed,
          hovered && Platform.OS === 'web' && { backgroundColor: theme.background.cardElevated },
        ]}
      >
        <FriendAvatar
          avatarId={avatarId}
          avatarConfig={currentAvatarConfig}
          fallbackUrl={activeAccount?.avatarUrl ?? undefined}
          size={24}
          nameForInitials={activeAccount?.displayName ?? activeAccount?.email ?? label}
          avatarRounded
          small
        />
        <Text style={[styles.label, { color: theme.gray[900] }]} numberOfLines={1}>Switch</Text>
        <ChevronsUpDown size={16} color={theme.gray[500]} strokeWidth={2.2} />
      </Pressable>
      <OrgSwitcherSheet visible={open} onClose={handleClose} closeHostThen={closeHostThen} />
    </>
  )
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  buttonPressed: { opacity: 0.85 },
  label: { fontSize: 14, fontWeight: '700' },
})
